import type { MooringAnchor } from "./Dock";

export const PLAYER_ANCHOR_ID = "player_anchor";

export interface DeployedAnchorOptions {
  x: number;
  z: number;
  waterLevel: number;
  depth: number;
}

export interface DeployedAnchorStepOptions {
  dt: number;
  waterLevel: number;
  depth: number;
  chainLength: number;
  bowHeightAboveWater?: number;
  rodeWeightKgPerM?: number;
}

export class DeployedAnchor implements MooringAnchor {
  readonly anchorId = PLAYER_ANCHOR_ID;

  private worldX: number;
  private worldY: number;
  private worldZ: number;
  private forceX = 0;
  private forceY = 0;
  private forceZ = 0;

  tension = 0;
  holdingState: "setting" | "holding" | "dragging" = "setting";

  constructor(opts: DeployedAnchorOptions) {
    this.worldX = opts.x;
    this.worldZ = opts.z;
    this.worldY = opts.waterLevel - Math.max(0.5, opts.depth);
  }

  setDepth(waterLevel: number, depth: number): void {
    this.worldY = waterLevel - Math.max(0.5, depth);
  }

  setHorizontalPosition(x: number, z: number): void {
    this.worldX = x;
    this.worldZ = z;
  }

  getWorldPos(): [number, number, number] {
    return [this.worldX, this.worldY, this.worldZ];
  }

  applyExternalForce(fx: number, fy: number, fz: number): void {
    this.forceX += fx;
    this.forceY += fy;
    this.forceZ += fz;
  }

  step(opts: DeployedAnchorStepOptions): void {
    this.setDepth(opts.waterLevel, opts.depth);
    const horizontalForce = Math.hypot(this.forceX, this.forceZ);
    this.tension = horizontalForce;

    const verticalDrop = Math.max(
      0.5,
      opts.depth + (opts.bowHeightAboveWater ?? 0.8),
    );
    const scope = opts.chainLength / verticalDrop;
    const holdingFactor = Math.max(0, Math.min(1, (scope - 2.5) / 4.5));
    const rodeWeightKgPerM = Math.max(0, opts.rodeWeightKgPerM ?? 0.08);
    const catenaryFactor = Math.max(
      0,
      Math.min(1, (rodeWeightKgPerM - 0.08) / 1.32),
    );
    const catenaryBonus = catenaryFactor * Math.max(0, scope - 2.5) * 850;
    const holdingLimit = 600 + holdingFactor * 5200 + catenaryBonus;

    if (horizontalForce < 80) {
      this.holdingState = "setting";
    } else if (horizontalForce <= holdingLimit) {
      this.holdingState = "holding";
    } else {
      this.holdingState = "dragging";
      const excess = horizontalForce - holdingLimit;
      const dragSpeed = Math.min(0.6, excess / 12000);
      const inv = horizontalForce > 1e-4 ? 1 / horizontalForce : 0;
      this.worldX += this.forceX * inv * dragSpeed * opts.dt;
      this.worldZ += this.forceZ * inv * dragSpeed * opts.dt;
    }

    this.forceX = 0;
    this.forceY = 0;
    this.forceZ = 0;
  }
}
